import { styled } from "@washingtonpost/wpds-ui-kit";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";
import { addOrcaEvent } from "../orca/utils";
import { OrcaWindowEvents } from "../globals";
import { useVideoDataContext } from "../orca/contexts/VideoDataContext";

export const ControlsContainer = ({
  children,
  togglePlay,
  controlsContainerHeight,
  setIsVisible,
  selected,
  debug
}) => {
  const { videoData } = useVideoDataContext();

  const timeoutRef = useRef(null);
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 3000);

    // Clear the timeout.
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [setIsVisible]);

  const handleMouseOver = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(true);
  }, [timeoutRef, setIsVisible]);

  const handleMouseLeave = useCallback(() => {
    if (!selected) {
      setIsVisible(false);
    }
  }, [selected, setIsVisible]);

  const handleFocus = () => {
    setIsVisible(true);
  };

  const handleTouchStart = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(true);
    addOrcaEvent(OrcaWindowEvents.CONTROLS_TOUCH, true, videoData._id, debug);
  }, [debug, videoData._id, setIsVisible]);

  const handleTouchEnd = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      addOrcaEvent(
        OrcaWindowEvents.CONTROLS_TOUCH,
        false,
        videoData._id,
        debug
      );
    }, 3000);
  }, [debug, videoData._id, setIsVisible]);

  return (
    <StyledContainer
      data-testid="orca-controls"
      tabIndex={0}
      aria-label="Video controls"
      onClick={togglePlay}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      // Only on mobile update controls max-height
      style={{
        "@sm": {
          maxHeight: controlsContainerHeight
        }
      }}
    >
      {children}
    </StyledContainer>
  );
};

ControlsContainer.propTypes = {
  children: PropTypes.node,
  togglePlay: PropTypes.func,
  controlsContainerHeight: PropTypes.string,
  setIsVisible: PropTypes.func,
  selected: PropTypes.bool,
  debug: PropTypes.bool
};

export const StyledContainer = styled("div", {
  top: "0",
  left: "0",
  position: "absolute",
  height: "100%",
  width: "100%",
  display: "flex",
  zIndex: "2",
  opacity: 1,
  "& .fade-out": {
    opacity: 0
  }
});
